import { FillPaint, PointLike, VectorSource } from "mapbox-gl";
import { GeoJSONSource, MapboxPolygonFeature, PolygonFeature } from "@src/components/map-view/map.types";
import { FillLayer, MapboxMap, MapStyle } from "react-map-gl";
import turfUnion from "@turf/union";
import { featureCollection } from "@turf/helpers";

export type FeaturesBySection = Map<string, PolygonFeature>;

export const calculateBounds = (mapStyle: MapStyle): [number, number, number, number] | undefined => {
	const busterSource = mapStyle.sources["buster"] as VectorSource | undefined;

	const bounds = busterSource?.bounds;

	if (!bounds || bounds.length !== 4) return undefined;

	return bounds as [number, number, number, number];
};

export const VENUE_FIT_PADDING = 24;

export const calculateFeatureBounds = (features: Array<MapboxPolygonFeature>): [number, number, number, number] | undefined => {
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;

	const visit = (coordinates: unknown) => {
		if (!Array.isArray(coordinates)) return;

		if (typeof coordinates[0] === "number" && typeof coordinates[1] === "number") {
			const [lng, lat] = coordinates as [number, number];
			if (!isFinite(lng) || !isFinite(lat)) return;

			minLng = Math.min(minLng, lng);
			minLat = Math.min(minLat, lat);
			maxLng = Math.max(maxLng, lng);
			maxLat = Math.max(maxLat, lat);
			return;
		}

		coordinates.forEach(visit);
	};

	features.forEach((feature) => visit(feature.geometry?.coordinates));

	if (minLng === Infinity || minLng === maxLng || minLat === maxLat) return undefined;

	return [minLng, minLat, maxLng, maxLat];
};

export const isJustRepeatedLetters = (token: string) => new Set(token.split("")).size === 1;

export const capitalizeToken = (token: string) =>
	isJustRepeatedLetters(token) ? token.toUpperCase() : token.slice(0, 1).toUpperCase() + token.slice(1);

const STRIP_PREFIX_ALIASES = new Set(["section", "sec", "floor", "fl", "level", "lvl", "ga"]);

const PREFIX_ALIASES: Record<string, string> = {
	balcony: "balc",
};

export const normalizeName = (name: string) => {
	const tokens = name
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[()_[\].,\\/|-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.split(" ")
		.filter(Boolean);

	while (tokens.length > 1 && STRIP_PREFIX_ALIASES.has(tokens[0])) {
		tokens.shift();
	}

	if (tokens.length > 0 && PREFIX_ALIASES[tokens[0]]) {
		tokens[0] = PREFIX_ALIASES[tokens[0]];
	}

	return tokens.map(capitalizeToken).join(" ");
};

export const mergePolygonFeatures = (...features: Array<PolygonFeature>) => {
	if (!features.length) return undefined;

	return features.reduce(
		(acc: PolygonFeature, feature) =>
			turfUnion(featureCollection([acc, feature]), {
				properties: {
					...feature.properties,
				},
			}) as PolygonFeature,
		features[0],
	);
};

export const queryMapPolygonFeatures = (
	point: PointLike | undefined,
	map: MapboxMap,
	layer: "section" | "row",
	name?: string,
) => {
	const layers = [layer === "section" ? "map-shapes-section" : "map-shapes-row"];

	const filter = ["all", ["==", ["geometry-type"], "Polygon"]];

	if (name) filter.push(["==", ["get", "name"], name]);

	return map.queryRenderedFeatures(point, {
		layers,
		filter,
	}) as Array<MapboxPolygonFeature>;
};

export const groupFeaturesBySection = (mapboxSectionFeatures: Array<MapboxPolygonFeature>) => {
	return mapboxSectionFeatures.reduce<FeaturesBySection>((acc: FeaturesBySection, mapboxSectionFeature) => {
		const normalizedSectionName = normalizeName(mapboxSectionFeature.properties.name);

		const sectionFeature = acc.get(normalizedSectionName);

		if (!sectionFeature) {
			return acc.set(normalizedSectionName, mapboxSectionFeature);
		}

		const mergedSectionFeature = mergePolygonFeatures(sectionFeature, mapboxSectionFeature);

		return mergedSectionFeature ? acc.set(normalizedSectionName, mergedSectionFeature) : acc;
	}, new Map());
};

export const createFillLayer = (id: string, paint: FillPaint, features: PolygonFeature | Array<PolygonFeature>): FillLayer => {
	const source: GeoJSONSource = Array.isArray(features)
		? { type: "geojson", data: { type: "FeatureCollection", features } }
		: { type: "geojson", data: { id, type: "Feature", geometry: features.geometry, properties: features.properties } };

	return {
		id,
		type: "fill",
		paint,
		source,
	};
};
